import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";

const empty = { title: "", slug: "", excerpt: "", category: "YouTube Marketing", tags: [], cover: "", content: "", seo_title: "", seo_description: "", published: false };

function slugify(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 70); }

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [seoLoading, setSeoLoading] = useState(false);
  const [aiKeywords, setAiKeywords] = useState("");

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    setLoading(true);
    const payload = { ...form, slug: form.slug || slugify(form.title), tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editId) await api.put(`/admin/blog/${editId}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed to save"); } finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const generateAI = async () => {
    if (!form.title.trim()) { toast.error("Enter a title/topic first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { topic: form.title, keywords: aiKeywords });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        content: data.content || f.content,
        seo_title: data.seo_title || f.seo_title,
        seo_description: data.seo_description || f.seo_description,
        category: data.category || f.category,
        tags: Array.isArray(data.tags) && data.tags.length ? data.tags : f.tags,
        slug: f.slug || slugify(data.title || f.title),
        published: false,
      }));
      toast.success("Draft generated — review, edit, then publish.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI generation failed");
    } finally { setAiLoading(false); }
  };

  const generateSEO = async () => {
    if (!form.title.trim()) { toast.error("Add a title first."); return; }
    setSeoLoading(true);
    try {
      const { data } = await api.post("/admin/blog/seo-meta", { title: form.title, excerpt: form.excerpt, content: form.content });
      setForm((f) => ({ ...f, seo_title: data.seo_title || f.seo_title, seo_description: data.seo_description || f.seo_description }));
      toast.success("SEO meta generated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI generation failed");
    } finally { setSeoLoading(false); }
  };
  const togglePublish = async (p) => { await api.put(`/admin/blog/${p.id}`, { ...p, published: !p.published }); load(); };

  return (
    <div>
      <SEO title="Blog CMS | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">Blog CMS</h1><p className="text-slate-400 text-sm">{items.length} posts</p></div>
        <Button onClick={openNew} data-testid="blog-new"><Plus className="w-4 h-4 mr-1" /> New post</Button>
      </div>

      <div className="mt-5 space-y-2">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            {p.cover && <img src={p.cover} alt="" className="w-14 h-14 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{p.title}</p>
              <p className="text-xs text-slate-400 truncate">{p.category} · /{p.slug}</p>
            </div>
            <Badge className={p.published ? "bg-emerald-500/20 text-emerald-300 cursor-pointer" : "bg-white/10 text-slate-400 cursor-pointer"} onClick={() => togglePublish(p)}>{p.published ? "Published" : "Draft"}</Badge>
            {p.published && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8"><ExternalLink className="w-4 h-4" /></Button></a>}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm">No posts yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={set("title")} data-testid="blog-title" required /></div>
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-primary"><Sparkles className="w-4 h-4" /> AI draft assistant (ChatGPT)</div>
              <p className="text-xs text-slate-500">Set a title above, add optional keywords, then generate a compliant first draft to review & edit. Nothing publishes automatically.</p>
              <Input value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} placeholder="Optional keywords, e.g. youtube ads, audience targeting" data-testid="blog-ai-keywords" />
              <Button type="button" variant="secondary" onClick={generateAI} disabled={aiLoading} data-testid="blog-ai-generate">
                {aiLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-1" /> Generate with AI</>}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Slug</Label><Input value={form.slug} onChange={set("slug")} placeholder="auto from title" /></div>
              <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={set("category")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Cover image URL</Label><Input value={form.cover} onChange={set("cover")} /></div>
            <div className="space-y-1.5"><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} /></div>
            <div className="space-y-1.5"><Label>Tags (comma separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value.split(",").map((t) => t.trim()) }))} /></div>
            <div className="space-y-1.5"><Label>Content (Markdown)</Label><Textarea rows={10} value={form.content} onChange={set("content")} className="font-mono text-xs" data-testid="blog-content" /></div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">SEO metadata</Label>
              <Button type="button" size="sm" variant="secondary" onClick={generateSEO} disabled={seoLoading} data-testid="blog-seo-generate">
                {seoLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Generating…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Auto-fill with AI</>}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>SEO title</Label><Input value={form.seo_title} onChange={set("seo_title")} /></div>
              <div className="space-y-1.5"><Label>SEO description</Label><Input value={form.seo_description} onChange={set("seo_description")} /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={!!form.published} onCheckedChange={set("published")} data-testid="blog-published" /><Label>Published</Label></div>
            <DialogFooter><Button type="submit" disabled={loading} data-testid="blog-save">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save post"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
